import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, SearchableSelect } from '@elka/ui';
import { AlertCircle } from 'lucide-react';
import type { Deal } from '@elka/types';
import { useCompanies } from '../../quotes/hooks/useCompanies';
import { useDuplicateDeal } from '../hooks/useDuplicateDeal';

interface DuplicateDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: Pick<Deal, 'id' | 'name' | 'companyId' | 'companyName'>;
}

export function DuplicateDealModal({ isOpen, onClose, deal }: DuplicateDealModalProps) {
  const { companies, addCompany } = useCompanies();
  const { duplicateDeal } = useDuplicateDeal();
  const navigate = useNavigate();

  // Callers mount this modal only while open, so initial state is the reset.
  const [name, setName] = useState(`${deal.name} - עותק`);
  const [companyId, setCompanyId] = useState(deal.companyId);
  const [companyName, setCompanyName] = useState(deal.companyName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.name })),
    [companies]
  );

  const handleSelectCompany = (value: string) => {
    const company = companies.find((c) => c.id === value);
    setCompanyId(value);
    setCompanyName(company?.name || '');
  };

  const handleCreateCompany = async (createdName: string) => {
    setError(null);
    try {
      const created = await addCompany({ name: createdName });
      setCompanyId(created.id);
      setCompanyName(created.name);
    } catch (err) {
      console.error('[DuplicateDealModal] addCompany failed:', err);
      setError('יצירת החברה נכשלה. נסה שוב.');
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !companyId) return;
    setSubmitting(true);
    setError(null);
    try {
      const newDealId = await duplicateDeal(deal.id, {
        name: name.trim(),
        companyId,
        companyName,
      });
      onClose();
      navigate(`/deals/${newDealId}`);
    } catch (err) {
      console.error('[DuplicateDealModal] duplicateDeal failed:', err);
      setError('שכפול העסקה נכשל. נסה שוב.');
      setSubmitting(false);
    }
  };

  const canSubmit = !!name.trim() && !!companyId && !submitting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="שכפול עסקה"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            ביטול
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={!canSubmit}
          >
            שכפל עסקה
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-blue-50 p-3 text-xs text-[#3C5FA8]">
          כתב הכמויות, התמחור וההצעה האחרונה יועתקו כטיוטה חדשה. בקשות מחיר וחשבונות לא מועתקים.
        </div>

        <Input label="שם העסקה" value={name} onChange={(e) => setName(e.target.value)} required />

        <SearchableSelect
          label="חברה (לקוח)"
          options={companyOptions}
          value={companyId}
          onChange={handleSelectCompany}
          placeholder="חפש חברה..."
          required
          clearable
          creatable
          onCreateOption={handleCreateCompany}
        />

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
